// ABOUTME: The message being written in each item's discussion, kept outside the window that shows it: a draft lives
// ABOUTME: until it is sent or dropped with "Не отправлять", through a window moved to another day, closed or reopened.
import type { ApolloClient } from "@apollo/client"
import { AssetKind, AssetUploadImageDocument, TextFormat } from "@/graphql/main/gql/graphql"
import { fileRefusalOf, type FileLimits } from "@/model/assets/fileTypes"
import { uploadFile } from "@/model/assets/storageUpload"
import { onUserSwitch } from "@/model/user/userSwitch"
import type { FormattedText } from "@shared/ui/richText/markdown"
import { holdUnsavedWork } from "@shared/utils/unsavedWork"
import type { AttachedAsset } from "./attachedAsset"
import { itemListeners } from "./itemListeners"
import { canSendMessage, isBlankText, MESSAGE_MAX_ATTACHMENTS } from "./messageText"
import { sendMessage } from "./outbox"
import { handedBack, type PickedAttachment, type PickedFile, type PickedPicture } from "./pickedAttachment"

/** A refused message handed back to be fixed: its text and the attachments it still can carry. */
export interface RefusedMessage {
  text: string
  format: TextFormat
  attachments: readonly AttachedAsset[]
}

export interface MessageDraft {
  /** The draft's number: new for one begun in an empty field — a refusal standing into it too — and kept through
   * every edit, move and close of the window. A save answers the draft it was pressed with, not a later one. */
  readonly id: string
  /** Grows with every edit of what the message carries — its text, a pick, a removal — not with an upload. */
  readonly revision: number
  /** Who writes it: another person signing in on the tab erases it. */
  readonly owner: string
  readonly text: FormattedText
  readonly picked: readonly PickedAttachment[]
  /** Refused messages waiting for the field to empty, oldest first, with what refused them. */
  readonly waiting: readonly { message: RefusedMessage; reason: unknown }[]
  /** What refused the message that stood into the field from the queue, not shown yet; null once shown. */
  readonly refusal: unknown
}

const EMPTY_TEXT: FormattedText = { text: "", format: TextFormat.Plain }

const drafts = new Map<string, MessageDraft>()
const listeners = itemListeners()
const everyListener = new Set<() => void>()
/** How many discussion sections show each item's draft now. */
const shown = new Map<string, number>()
/** The picked files' object URLs by attachment key: let go with the attachment, not with the window. */
const objectUrls = new Map<string, string>()
/** The uploads into storage on their way, by attachment key: stopped with the attachment, not with the window. */
const uploads = new Map<string, AbortController>()
let releaseHold: (() => void) | null = null

export const draftOf = (itemId: string): MessageDraft | undefined => drafts.get(itemId)

export const subscribeDraft = listeners.subscribe

/** Whoever shows what any draft is like — a window's question — hears when one changes. */
export function subscribeAllDrafts(listener: () => void): () => void {
  everyListener.add(listener)
  return () => {
    everyListener.delete(listener)
  }
}

const isEmpty = (draft: Pick<MessageDraft, "text" | "picked">): boolean =>
  isBlankText(draft.text.text) && draft.picked.length === 0

/** The uploaded attachments, in the order they were picked: what the message carries. */
export const attachedOf = (draft: MessageDraft): AttachedAsset[] =>
  draft.picked.flatMap((item): AttachedAsset[] => (item.status === "uploaded" && item.asset ? [item.asset] : []))

/** Ready to go: within the limit, and every attachment uploaded — none still on its way, nor failed. */
export const isDraftReady = (draft: MessageDraft): boolean =>
  canSendMessage({ text: draft.text.text, attachmentCount: attachedOf(draft).length }) &&
  draft.picked.every((item) => item.status === "uploaded")

/** Whether a discussion section shows the item's draft now: the person may be writing it. */
export const isDraftShown = (itemId: string): boolean => (shown.get(itemId) ?? 0) > 0

const newDraft = (owner: string): MessageDraft => ({
  id: crypto.randomUUID(),
  revision: 0,
  owner,
  text: EMPTY_TEXT,
  picked: [],
  waiting: [],
  refusal: null,
})

const fromRefused = (owner: string, message: RefusedMessage): MessageDraft => ({
  ...newDraft(owner),
  text: { text: message.text, format: message.format },
  picked: message.attachments.map(handedBack),
})

function publish(itemId: string, next: MessageDraft | undefined): void {
  if (next === undefined && !drafts.has(itemId)) return
  if (next === undefined) drafts.delete(itemId)
  else drafts.set(itemId, next)
  // A draft is work the person has not sent: a silent update must not reload it away.
  if (drafts.size > 0 && releaseHold === null) releaseHold = holdUnsavedWork()
  if (drafts.size === 0 && releaseHold !== null) {
    releaseHold()
    releaseHold = null
  }
  listeners.notify(itemId)
  everyListener.forEach((listener) => listener())
}

/** Writes the item's draft. One emptied — sent, dropped or wiped by hand — goes, and the next refused message waiting
 * stands into the field instead, its reason to be shown again. */
function write(itemId: string, next: MessageDraft): void {
  if (!isEmpty(next)) {
    publish(itemId, next)
    return
  }
  const [first, ...rest] = next.waiting
  publish(
    itemId,
    first === undefined
      ? undefined
      : { ...fromRefused(next.owner, first.message), waiting: rest, refusal: first.reason },
  )
}

/** The attachment goes: its upload into storage stops, and its picked file's address is let go. */
const letGo = (key: string): void => {
  uploads.get(key)?.abort()
  uploads.delete(key)
  const url = objectUrls.get(key)
  if (url === undefined) return
  URL.revokeObjectURL(url)
  objectUrls.delete(key)
}

const letGoAll = (draft: MessageDraft): void => draft.picked.forEach((item) => letGo(item.key))

export function setDraftText(owner: string, itemId: string, text: FormattedText): void {
  const draft = drafts.get(itemId) ?? newDraft(owner)
  write(itemId, { ...draft, text, revision: draft.revision + 1 })
}

/** An upload's news, laid over the attachment `key`: what the message carries stays the same, so it is no edit. */
function settle(itemId: string, key: string, update: (item: PickedAttachment) => PickedAttachment): void {
  const draft = drafts.get(itemId)
  // An answer for an attachment removed meanwhile, or for a draft gone since — sent, dropped, erased — changes
  // nothing: a key is the one pick's, so no draft after holds it. A picture's upload, attached to no message, goes
  // with the day's cleanup; a file's was stopped with its attachment.
  if (!draft?.picked.some((item) => item.key === key)) return
  publish(itemId, { ...draft, picked: draft.picked.map((item) => (item.key === key ? update(item) : item)) })
}

const settlePicture = (
  itemId: string,
  key: string,
  change: Partial<Pick<PickedPicture, "status" | "asset" | "error">>,
) => settle(itemId, key, (item) => (item.kind === AssetKind.Image ? { ...item, ...change } : item))

const settleFile = (
  itemId: string,
  key: string,
  change: Partial<Pick<PickedFile, "status" | "asset" | "error" | "progress" | "attempt">>,
) => settle(itemId, key, (item) => (item.kind === AssetKind.Image ? item : { ...item, ...change }))

/** Uploads a picture the moment it is picked (assetUploadImage, PRIVATE); its answer lands with no window open too. */
function uploadPicture(client: ApolloClient, itemId: string, key: string, file: File): void {
  void client
    .mutate({ mutation: AssetUploadImageDocument, variables: { file } })
    .then(({ data }) => {
      if (!data) return
      const { id, path, width, height } = data.assetUploadImage
      settlePicture(itemId, key, {
        status: "uploaded",
        asset: { kind: AssetKind.Image, id, path, width, height },
        error: null,
      })
    })
    .catch((error: unknown) => settlePicture(itemId, key, { status: "failed", error }))
}

/**
 * Uploads a picked file into storage; its news lands in the draft with no window open too (WA-928). The tile shows
 * whole percents: the draft hears of one when it, or the phase, changes.
 */
function uploadPickedFile(client: ApolloClient, itemId: string, { key, file, attempt }: PickedFile): void {
  if (file === null || attempt === null) return
  const controller = new AbortController()
  uploads.set(key, controller)
  let told = { phase: "", percent: -1 }
  void uploadFile({
    client,
    file,
    attempt,
    signal: controller.signal,
    onProgress: (progress) => {
      const percent = Math.floor((progress.sentBytes * 100) / file.size)
      if (progress.phase === told.phase && percent === told.percent) return
      told = { phase: progress.phase, percent }
      settleFile(itemId, key, { progress })
    },
    onAttempt: (next) => settleFile(itemId, key, { attempt: next }),
  })
    .then((asset) =>
      settleFile(itemId, key, {
        status: "uploaded",
        progress: null,
        error: null,
        asset: {
          kind: AssetKind.File,
          id: asset.id,
          name: asset.file?.name ?? file.name,
          sizeBytes: asset.sizeBytes,
          contentType: asset.contentType,
        },
      }),
    )
    .catch((error: unknown) => {
      // Stopped with its attachment: there is nobody to tell.
      if (controller.signal.aborted) return
      settleFile(itemId, key, { status: "failed", progress: null, error })
    })
    .finally(() => {
      if (uploads.get(key) === controller) uploads.delete(key)
    })
}

/** Adds what fits the message's room of attachments, as one edit; answers what was added and how many were left out. */
function addPicked<T extends PickedAttachment>(
  owner: string,
  itemId: string,
  files: readonly File[],
  toPicked: (file: File) => T,
): { added: T[]; leftOut: number } {
  const draft = drafts.get(itemId) ?? newDraft(owner)
  const taken = files.slice(0, Math.max(0, MESSAGE_MAX_ATTACHMENTS - draft.picked.length))
  const added = taken.map(toPicked)
  if (added.length > 0) write(itemId, { ...draft, picked: [...draft.picked, ...added], revision: draft.revision + 1 })
  return { added, leftOut: files.length - taken.length }
}

/** Picks the pictures `files`, as many as the message still has room for; answers how many were left out. */
export function pickPictures(client: ApolloClient, owner: string, itemId: string, files: readonly File[]): number {
  const { added, leftOut } = addPicked(owner, itemId, files, (file): PickedPicture => {
    const key = crypto.randomUUID()
    const preview = URL.createObjectURL(file)
    objectUrls.set(key, preview)
    return { key, kind: AssetKind.Image, preview, status: "uploading", asset: null, error: null, file }
  })
  added.forEach((item) => {
    if (item.file) uploadPicture(client, itemId, item.key, item.file)
  })
  return leftOut
}

/**
 * Picks the files `files`, as many as the message still has room for; answers how many were left out. Each uploads into
 * storage at once, two at a time. One the server would refuse — its type, no bytes, its size by the uploader's limits —
 * stays a tile with the reason and goes nowhere.
 */
export function pickFiles(
  client: ApolloClient,
  owner: string,
  itemId: string,
  files: readonly File[],
  limits: FileLimits,
): number {
  const { added, leftOut } = addPicked(owner, itemId, files, (file): PickedFile => {
    const refusal = fileRefusalOf(file, limits)
    const picked = {
      key: crypto.randomUUID(),
      kind: AssetKind.File,
      name: file.name,
      sizeBytes: file.size,
      contentType: file.type,
      asset: null,
    } as const
    return refusal === null
      ? {
          ...picked,
          status: "uploading",
          error: null,
          file,
          progress: { phase: "queued", sentBytes: 0 },
          attempt: { clientUploadId: crypto.randomUUID(), assetId: null },
        }
      : { ...picked, status: "failed", error: refusal, file: null, progress: null, attempt: null }
  })
  added.forEach((item) => uploadPickedFile(client, itemId, item))
  return leftOut
}

export function removeAttachment(itemId: string, key: string): void {
  const draft = drafts.get(itemId)
  if (draft === undefined) return
  letGo(key)
  write(itemId, { ...draft, picked: draft.picked.filter((item) => item.key !== key), revision: draft.revision + 1 })
}

/**
 * Uploads a failed attachment again: what the message carries stays the same, so it is no edit. A file goes on with
 * its attempt: the bytes storage holds already are not sent again.
 */
export function retryAttachment(client: ApolloClient, itemId: string, key: string): void {
  const item = drafts.get(itemId)?.picked.find((candidate) => candidate.key === key)
  if (!item?.file) return
  if (item.kind === AssetKind.Image) {
    settlePicture(itemId, key, { status: "uploading", error: null })
    uploadPicture(client, itemId, key, item.file)
    return
  }
  const retried = { status: "uploading", error: null, progress: { phase: "queued", sentBytes: 0 } } as const
  settleFile(itemId, key, retried)
  uploadPickedFile(client, itemId, { ...item, ...retried })
}

/**
 * Sends the message written for the item — from its field, or from its window's save — and empties the field. A
 * message not ready to go stays: false.
 */
export function sendDraft(client: ApolloClient, itemId: string): boolean {
  const draft = drafts.get(itemId)
  if (draft === undefined || !isDraftReady(draft)) return false
  sendMessage(client, itemId, { text: draft.text.text, format: draft.text.format, attachments: attachedOf(draft) })
  letGoAll(draft)
  write(itemId, { ...draft, text: EMPTY_TEXT, picked: [] })
  return true
}

/** "Не отправлять": the message is dropped, and a refused one waiting stands into the field. */
export function discardDraft(itemId: string): void {
  const draft = drafts.get(itemId)
  if (draft === undefined) return
  letGoAll(draft)
  write(itemId, { ...draft, text: EMPTY_TEXT, picked: [] })
}

/**
 * A refused message comes back to be fixed — whole, into an empty field, under a number of its own. Into a field being
 * written it does not go: it waits behind, never over what is being written, and refusals handed over one after
 * another (a window opening on two kept while it was closed) come back one at a time. One with nothing left to fix —
 * pictures alone, taken by the day's cleanup — comes back nowhere: the window showed its reason as it came, and an
 * empty draft waiting in the queue would hold the field, the window's question and the tab's updates.
 */
export function restoreRefused(owner: string, itemId: string, message: RefusedMessage, reason: unknown): void {
  const refused = fromRefused(owner, message)
  if (isEmpty(refused)) return
  // A draft kept is never empty: emptied, it goes, or the next refusal waiting stands into the field (write).
  const draft = drafts.get(itemId)
  if (draft === undefined) publish(itemId, refused)
  else publish(itemId, { ...draft, waiting: [...draft.waiting, { message, reason }] })
}

/** The reason of the refusal that stood into the field has been shown. */
export function acknowledgeRefusal(itemId: string): void {
  const draft = drafts.get(itemId)
  if (draft !== undefined && draft.refusal !== null) publish(itemId, { ...draft, refusal: null })
}

/** A discussion section shows the item's draft; the returned function says it no longer does. */
export function showDraft(itemId: string): () => void {
  shown.set(itemId, (shown.get(itemId) ?? 0) + 1)
  let hidden = false
  return () => {
    if (hidden) return
    hidden = true
    const left = (shown.get(itemId) ?? 1) - 1
    if (left > 0) shown.set(itemId, left)
    else shown.delete(itemId)
  }
}

/** The item is gone: its draft goes, with what waits behind it. */
export function forgetDraft(itemId: string): void {
  const draft = drafts.get(itemId)
  if (draft === undefined) return
  letGoAll(draft)
  publish(itemId, undefined)
}

/** The item is gone, but a section on screen keeps its text to be copied (§14): that section erases it as it goes. */
export function forgetHiddenDraft(itemId: string): void {
  if (!isDraftShown(itemId)) forgetDraft(itemId)
}

function forgetAllDrafts(): void {
  Array.from(drafts.keys()).forEach(forgetDraft)
}

// Another person signed in on the tab: what the one before was writing is theirs alone.
onUserSwitch(forgetAllDrafts)

/** Test seam — the drafts outlive a test otherwise. Their listeners go first: the test's sections are unmounting, and
 * nothing the reset changes is theirs to hear. */
export function resetMessageDraftsForTests(): void {
  listeners.clear()
  everyListener.clear()
  forgetAllDrafts()
  shown.clear()
  objectUrls.clear()
}
